import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Avatar,
  LinearProgress,
  IconButton,
  Badge,
  Fab,
  Stack
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  DateRange,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet,
  Savings,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  NotificationsNone,
  WavingHand
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/redux';
import { transactionsApi } from '../services/api';
import { Transaction } from '../types';
import { useNavigate } from 'react-router-dom';
import YearOverYearChart from '../components/charts/YearOverYearChart';
import MonthOverMonthChart from '../components/charts/MonthOverMonthChart';
import SpendingVelocityChart from '../components/charts/SpendingVelocityChart';
import MonthlyCashFlowChart from '../components/charts/MonthlyCashFlowChart';
import CategoryComparisonChart from '../components/charts/CategoryComparisonChart';
import FinancialHealthScore from '../components/charts/FinancialHealthScore';
import CategoryTrendsChart from '../components/charts/CategoryTrendsChart';
import BudgetVsActualChart from '../components/charts/BudgetVsActualChart';
import TopCategoriesTrendsChart from '../components/charts/TopCategoriesTrendsChart';
import BudgetProgressRings from '../components/charts/BudgetProgressRings';

export default function Dashboard() {
  const navigate = useNavigate();
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [monthlyStats, setMonthlyStats] = useState({
    totalSpent: 0,
    totalIncome: 0,
    transactionCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch recent transactions
        const transactions = await transactionsApi.getTransactions();
        setRecentTransactions(transactions.slice(0, 5)); // Show last 5 transactions
        
        // Calculate monthly stats
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        
        const monthlyTransactions = transactions.filter((t: Transaction) => {
          const transactionDate = new Date(t.transaction_date);
          return transactionDate.getMonth() === currentMonth && 
                 transactionDate.getFullYear() === currentYear;
        });
        
        const totalSpent = monthlyTransactions
          .filter((t: Transaction) => t.amount < 0)
          .reduce((sum: number, t: Transaction) => sum + Math.abs(t.amount), 0);
        
        const totalIncome = monthlyTransactions
          .filter((t: Transaction) => t.amount > 0)
          .reduce((sum: number, t: Transaction) => sum + t.amount, 0);
        
        setMonthlyStats({
          totalSpent,
          totalIncome,
          transactionCount: monthlyTransactions.length
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const netWorth = monthlyStats.totalIncome - monthlyStats.totalSpent;
  const savingsRate = monthlyStats.totalIncome > 0 ? 
    ((monthlyStats.totalIncome - monthlyStats.totalSpent) / monthlyStats.totalIncome) * 100 : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 0
        }}
      >
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WavingHand sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Welcome back!
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Here's your financial overview for {currentMonth}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton sx={{ color: 'white' }}>
                <Badge badgeContent={3} color="error">
                  <NotificationsNone />
                </Badge>
              </IconButton>
              <IconButton sx={{ color: 'white' }}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          {/* Main Metrics Hero Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 300 }}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Net Worth
                  </Typography>
                  <AccountBalanceWallet sx={{ color: 'rgba(255,255,255,0.8)' }} />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  {formatCurrency(netWorth)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {netWorth >= 0 ? <ArrowUpward sx={{ color: '#4ade80', mr: 0.5, fontSize: 16 }} /> 
                                 : <ArrowDownward sx={{ color: '#f87171', mr: 0.5, fontSize: 16 }} />}
                  <Typography variant="body2" sx={{ color: netWorth >= 0 ? '#4ade80' : '#f87171' }}>
                    {netWorth >= 0 ? 'Positive' : 'Deficit'} this month
                  </Typography>
                </Box>
              </Paper>
            </Box>
            
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 300 }}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Savings Rate
                  </Typography>
                  <Savings sx={{ color: 'rgba(255,255,255,0.8)' }} />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  {Math.round(savingsRate)}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(Math.max(savingsRate, 0), 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: savingsRate >= 20 ? '#4ade80' : savingsRate >= 10 ? '#fbbf24' : '#f87171'
                    }
                  }} 
                />
              </Paper>
            </Box>
            
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: 300 }}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Transactions
                  </Typography>
                  <ReceiptIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  {monthlyStats.transactionCount}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {monthlyStats.transactionCount > 0 ? 'Active month' : 'No activity yet'}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        {/* Quick Actions */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => navigate('/transactions')}
              sx={{ 
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Add Transaction
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ScheduleIcon />}
              onClick={() => navigate('/recurring-transactions')}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Recurring Payments
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<CategoryIcon />}
              onClick={() => navigate('/categories')}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Manage Categories
            </Button>
          </Stack>
        </Paper>

        {/* Budget Progress Rings */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <BudgetProgressRings />
        </Paper>


        {/* Income vs Expenses Breakdown */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, minWidth: 300 }}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Monthly Income
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                {formatCurrency(monthlyStats.totalIncome)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total income received this month
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, minWidth: 300 }}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingDown sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Monthly Expenses
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
                {formatCurrency(monthlyStats.totalSpent)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total expenses for this month
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Charts and Analytics Section */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Financial Analytics
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/analytics')}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              View Reports
            </Button>
          </Box>

          {/* Primary Charts */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 3 }, mb: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', lg: '2 1 65%' }, minWidth: 0 }}>
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <MonthlyCashFlowChart />
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 35%' }, minWidth: 0 }}>
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <SpendingVelocityChart />
              </Paper>
            </Box>
          </Box>

          {/* Secondary Charts */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 3 }, mb: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, minWidth: 0 }}>
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  overflow: 'hidden',
                  height: { xs: 350, sm: 400 }
                }}
              >
                <MonthOverMonthChart />
              </Paper>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '1 1 45%' }, 
              minWidth: 0,
              display: { xs: 'none', sm: 'none', md: 'block' }
            }}>
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  overflow: 'hidden',
                  height: { xs: 350, sm: 400 }
                }}
              >
                <CategoryComparisonChart />
              </Paper>
            </Box>
          </Box>

          {/* Additional Analytics */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, minWidth: 350 }}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <BudgetVsActualChart />
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, minWidth: 350 }}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CategoryTrendsChart />
              </Paper>
            </Box>
          </Box>

          {/* Top Categories Trends - Hidden on mobile */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'none', md: 'flex' }, 
            flexWrap: 'wrap', 
            gap: 3 
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', lg: '2 1 65%' }, minWidth: 400 }}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <TopCategoriesTrendsChart />
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 35%' }, minWidth: 300 }}>
              {/* Placeholder for future chart or keep empty for better layout */}
              <Box sx={{ height: { xs: 350, sm: 400 } }} />
            </Box>
          </Box>
        </Paper>

        {/* Recent Activity and Insights */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '2 1 65%' }, minWidth: 400 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: 500 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Recent Transactions
                </Typography>
                <Button 
                  variant="text" 
                  size="small"
                  onClick={() => navigate('/transactions')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  View All
                </Button>
              </Box>
              
              {recentTransactions.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: 350,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.100' }}>
                    <DateRange sx={{ fontSize: 32, color: 'text.secondary' }} />
                  </Avatar>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                    No transactions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Add your first transaction to start tracking your finances
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/transactions')}
                    sx={{ mt: 2, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    Add Transaction
                  </Button>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {recentTransactions.map((transaction, index) => (
                    <Box key={transaction.id} sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              mr: 2,
                              bgcolor: transaction.amount < 0 ? 'error.light' : 'success.light'
                            }}
                          >
                            {transaction.amount < 0 ? 
                              <ArrowDownward sx={{ color: 'error.main' }} /> : 
                              <ArrowUpward sx={{ color: 'success.main' }} />
                            }
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {transaction.description}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(transaction.transaction_date)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              color: transaction.amount < 0 ? 'error.main' : 'success.main'
                            }}
                          >
                            {transaction.amount < 0 ? '' : '+'}{formatCurrency(transaction.amount)}
                          </Typography>
                          {transaction.category && (
                            <Chip 
                              label={transaction.category.name}
                              size="small"
                              sx={{ 
                                backgroundColor: transaction.category.color,
                                color: 'white',
                                fontSize: '0.75rem',
                                height: 20,
                                mt: 0.5
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      {index < recentTransactions.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 35%' }, minWidth: 300 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: 500 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                Additional Insights
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <FinancialHealthScore />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <YearOverYearChart />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Floating Action Button */}
      <Fab 
        color="primary" 
        onClick={() => navigate('/transactions')}
        sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24,
          zIndex: 1000
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}